import type { FC } from "react";

import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { cn } from "@/lib/utils";

const SIZES = {
  /** The sidebar's Agents button. */
  button: {
    box: "size-5",
    single: "size-5 rounded-md",
    pair: "size-3.5 rounded-xs"
  },
  /** Banners and the Agents menu header. */
  banner: {
    box: "size-7",
    single: "size-7 rounded-lg",
    pair: "size-5 rounded-md"
  }
} as const;

/**
 * One agent face alone, or the top two overlapping corner to corner with the
 * front face ringed in the well's colour.
 */
export const FaceStack: FC<{
  seeds: string[];
  size?: keyof typeof SIZES;
  /** Ring colour for the front face (the well behind it). */
  ringClassName?: string;
  className?: string;
}> = ({
  seeds,
  size = "button",
  ringClassName = "ring-sidebar",
  className
}) => {
  const s = SIZES[size];
  const [first, second] = seeds;
  if (!first) return <span className={cn(s.box, className)} />;
  if (!second) {
    return (
      <AgentMoodAvatar
        agent={{ key: first }}
        className={cn(s.single, className)}
      />
    );
  }
  return (
    <span
      className={cn("relative block shrink-0", s.box, className)}
      data-testid="face-stack"
    >
      <AgentMoodAvatar
        varied
        agent={{ key: second }}
        className={cn("absolute top-0 left-0", s.pair)}
      />
      <AgentMoodAvatar
        varied
        agent={{ key: first }}
        className={cn(
          "absolute right-0 bottom-0 ring-cutout",
          s.pair,
          ringClassName
        )}
      />
    </span>
  );
};
